import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdQuiz, MdAdd, MdOpenInNew, MdSearch } from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import './Assessment.css';

const statusLabel = {
  assigned: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  evaluated: 'Evaluated',
};

const AssessmentsAssigned = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        // Always only returns assessments assigned to the current user
        // (including Super Admin — they only see their own assigned work here).
        const { data } = await api.get('/assessments/assigned');
        setItems(data.data || []);
      } catch {
        toast.error('Failed to load assigned assessments');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const creators = useMemo(() => {
    const map = new Map();
    items.forEach(({ assessment }) => {
      const id = assessment?.createdBy?._id || assessment?.createdBy;
      if (!id) return;
      map.set(String(id), assessment.createdBy?.name || 'Unknown');
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return items.filter(({ assessment, attempt }) => {
      if (statusFilter && attempt?.status !== statusFilter) return false;

      if (creatorFilter) {
        const cid = String(
          assessment?.createdBy?._id || assessment?.createdBy || '',
        );
        if (cid !== creatorFilter) return false;
      }

      if (from || to) {
        const created =
          assessment?.createdAt || attempt?.createdAt
            ? new Date(assessment?.createdAt || attempt?.createdAt)
            : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }

      if (!q) return true;
      const hay = [
        assessment?.title,
        assessment?.description,
        assessment?.createdBy?.name,
        assessment?.createdBy?.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, statusFilter, creatorFilter, dateFrom, dateTo]);

  const hasActiveFilters =
    Boolean(search.trim()) ||
    Boolean(statusFilter) ||
    Boolean(creatorFilter) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCreatorFilter('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="assessment-page">
      <div className="assessment-header">
        <div>
          <h1>
            <MdQuiz size={28} />
            Assigned Assessments
          </h1>
          <p className="assessment-subtitle">
            {isSuperAdmin
              ? 'Assessments assigned to you personally. System-wide assessments are under All Assessments.'
              : 'Assessments assigned to you. Enter the password to start; the timer begins after verification.'}
          </p>
        </div>
        <div className="assessment-actions">
          <Link to="/assessments/created" className="assessment-btn secondary">
            {isSuperAdmin ? 'All Assessments' : 'Created by me'}
          </Link>
          <Link to="/assessments/new" className="assessment-btn">
            <MdAdd size={18} />
            Create assessment
          </Link>
        </div>
      </div>

      {!loading && items.length > 0 && (
        <>
          <div className="assessment-filters-bar">
            <div className="assessment-search-wrap">
              <MdSearch size={20} />
              <input
                type="search"
                placeholder="Search title, creator, description…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search assigned assessments"
              />
            </div>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by attempt status"
            >
              <option value="">All statuses</option>
              <option value="assigned">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="evaluated">Evaluated</option>
            </select>
            {creators.length > 1 && (
              <select
                className="form-control"
                value={creatorFilter}
                onChange={(e) => setCreatorFilter(e.target.value)}
                aria-label="Filter by creator"
              >
                <option value="">All creators</option>
                {creators.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <div className="assessment-date-range">
              <label htmlFor="assigned-date-from">From</label>
              <input
                id="assigned-date-from"
                type="date"
                className="form-control"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                aria-label="Assigned from date"
              />
              <label htmlFor="assigned-date-to">To</label>
              <input
                id="assigned-date-to"
                type="date"
                className="form-control"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                aria-label="Assigned to date"
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                className="assessment-btn secondary"
                onClick={clearFilters}
              >
                Clear
              </button>
            )}
          </div>
          <p className="assessment-filter-meta">
            Showing {filtered.length} of {items.length} assigned assessment
            {items.length === 1 ? '' : 's'}
          </p>
        </>
      )}

      {loading ? (
        <div className="assessment-loading">
          <span className="spinner" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="assessment-empty">
          No assessments assigned to you yet.
        </div>
      ) : filtered.length === 0 ? (
        <div className="assessment-empty">
          No assessments match your search or filters.{' '}
          <button
            type="button"
            className="assessment-btn secondary"
            onClick={clearFilters}
            style={{ marginLeft: 8 }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="card assessment-table-card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Created by</th>
                  <th>Date &amp; time</th>
                  <th>Duration</th>
                  <th>Marks</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(({ assessment, attempt }) => (
                  <tr key={assessment._id}>
                    <td>
                      <div className="assessment-table-title">
                        {assessment.title}
                      </div>
                      {assessment.description ? (
                        <div className="assessment-table-sub">
                          {assessment.description}
                        </div>
                      ) : null}
                    </td>
                    <td>{assessment.createdBy?.name || '—'}</td>
                    <td>
                      {assessment.scheduledAt
                        ? new Date(assessment.scheduledAt).toLocaleString(
                            undefined,
                            { dateStyle: 'medium', timeStyle: 'short' },
                          )
                        : '—'}
                    </td>
                    <td>{assessment.durationMinutes} min</td>
                    <td>{assessment.totalMarks}</td>
                    <td>
                      <span className={`assessment-badge ${attempt.status}`}>
                        {statusLabel[attempt.status] || attempt.status}
                      </span>
                    </td>
                    <td>
                      {['submitted', 'evaluated'].includes(attempt.status)
                        ? `${attempt.totalScore ?? 0}/${assessment.totalMarks}`
                        : '—'}
                    </td>
                    <td>
                      <div className="assessment-table-actions">
                        {(() => {
                          const waiting =
                            attempt.status === 'assigned' &&
                            assessment.scheduledAt &&
                            Date.now() <
                              new Date(assessment.scheduledAt).getTime();
                          const label = waiting
                            ? `Opens ${new Date(
                                assessment.scheduledAt,
                              ).toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}`
                            : attempt.status === 'assigned'
                              ? 'Start'
                              : attempt.status === 'in_progress'
                                ? 'Continue'
                                : 'View result';
                          return (
                            <Link
                              to={`/assessments/${assessment._id}/take`}
                              className="assessment-btn sm icon-only"
                              title={label}
                              aria-label={label}
                            >
                              <MdOpenInNew size={18} />
                            </Link>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentsAssigned;
